import { DocumentUploadSource } from "../api/graphql/schema.generated";
import { StatusColor } from "../constants";
import {
  KontaxUserStatus,
  NextVatDeclarationStatus,
  OnbStatus,
} from "../types";

// Maps each raw DocumentUploadSource enum value to a badge color + i18n label
// key for the Receipt Overview Source column (SMT-903). Mapping signed off by
// Stella in the ticket comments (OQ-1 grouping, OQ-2 palette). Slices 3 (filter)
// and 7 (sort) consume the same helpers, so keep them as the single source of
// truth for source presentation.
export const getColorByUploadSource = (
  source?: DocumentUploadSource | null
): StatusColor => {
  switch (source) {
    case DocumentUploadSource.SMARTA_DOCUMENTS:
      return StatusColor.BLUE;
    case DocumentUploadSource.EMAIL:
      return StatusColor.ORANGE;
    case DocumentUploadSource.WEB:
      return StatusColor.PURPLE;
    case DocumentUploadSource.MOBILE:
      return StatusColor.GOLD;
    case DocumentUploadSource.BACKOFFICE:
      return StatusColor.CYAN;
    case DocumentUploadSource.EMAIL_FETCH:
      return StatusColor.GRAY;
    default:
      return StatusColor.GRAY;
  }
};

export const getLabelByUploadSource = (
  source?: DocumentUploadSource | null
): string => {
  switch (source) {
    case DocumentUploadSource.SMARTA_DOCUMENTS:
      return "source.integration";
    case DocumentUploadSource.EMAIL:
      return "source.email";
    case DocumentUploadSource.WEB:
      return "source.web";
    case DocumentUploadSource.MOBILE:
      return "source.mobile";
    case DocumentUploadSource.BACKOFFICE:
      return "source.taxops";
    case DocumentUploadSource.EMAIL_FETCH:
      return "source.fetch";
    default:
      return "source.unknown";
  }
};

// SMT-1109: `uploadSource` only says a document came from smarta-documents
// (SMARTA_DOCUMENTS -> "Integration"). The `documentOrigin` field added in
// SMT-1108 carries the exact origin: an accounting provider id, or the intake
// channel for OCR-pipeline receipts. Everything below is the single source of
// truth for turning that raw id into something Tax Ops can read.

// Mirrors target-systems.config.ts in smarta-documents. Id -> logo file is not
// 1:1 (buchhaltungsbutler-api -> buchhaltungsbutler.svg, qonto-sandbox reuses
// the Qonto logo), so the filename is spelled out per provider rather than
// derived from the id. Names are the short forms Tax Ops uses, not the longer
// official product names the target-systems API returns.
const ORIGIN_LOGO_BASE_URL =
  "https://cdn.smarta.tax/images/documents/target-systems";

const PROVIDER_ORIGINS: Record<string, { name: string; logoFile: string }> = {
  "lexware-office": { name: "Lexware Office", logoFile: "lexware-office.svg" },
  sevdesk: { name: "sevDesk", logoFile: "sevdesk.svg" },
  qonto: { name: "Qonto", logoFile: "qonto.svg" },
  "qonto-sandbox": { name: "Qonto Sandbox", logoFile: "qonto.svg" },
  "buchhaltungsbutler-api": {
    name: "BuchhaltungsButler",
    logoFile: "buchhaltungsbutler.svg",
  },
  pennylane: { name: "PennyLane", logoFile: "pennylane.svg" },
};

// Intake channels recorded by smarta-documents (SMT-1106). Kept separate from
// the `source.*` keys so a receipt from the new forwarding flow reads "E-Mail"
// while a legacy shared-inbox one reads "E-Mail (legacy)".
const CHANNEL_ORIGINS: Record<
  string,
  { labelKey: string; color: StatusColor }
> = {
  email: { labelKey: "origin.email", color: StatusColor.ORANGE },
  web: { labelKey: "origin.web", color: StatusColor.PURPLE },
  mobile: { labelKey: "origin.mobile", color: StatusColor.GOLD },
};

/*
 * Ids of the accounting providers, for consumers that enumerate them rather than
 * resolve one — the Integration group in the Receipt Overview source filter
 * (SMT-1191). Derived from the maps above rather than restated, so a provider
 * added there appears in the filter automatically and badge and filter cannot
 * drift apart. Insertion order is the display order.
 */
export const PROVIDER_ORIGIN_IDS = Object.keys(PROVIDER_ORIGINS);

/** Intake channels the OCR pipeline records, in the order Tax Ops reads them. */
export const CHANNEL_ORIGIN_IDS = Object.keys(CHANNEL_ORIGINS);

export type OriginPresentation =
  | { kind: "provider"; name: string; logoUrl: string }
  | { kind: "channel"; labelKey: string; color: StatusColor }
  | { kind: "unknown"; label: string };

/**
 * Resolves a raw `documentOrigin` id to how it should be presented.
 * Returns `undefined` when there is no origin (non-synced or pre-backfill
 * rows), so callers fall back to the coarse `uploadSource` badge.
 * An unrecognised id renders as itself — never masked behind "Integration".
 */
export const getOriginPresentation = (
  origin?: string | null
): OriginPresentation | undefined => {
  if (!origin) return undefined;

  const provider = PROVIDER_ORIGINS[origin];
  if (provider) {
    return {
      kind: "provider",
      name: provider.name,
      logoUrl: `${ORIGIN_LOGO_BASE_URL}/${provider.logoFile}`,
    };
  }

  const channel = CHANNEL_ORIGINS[origin];
  if (channel) {
    return { kind: "channel", ...channel };
  }

  return { kind: "unknown", label: origin };
};

export const getColorByUserStatus = (status?: KontaxUserStatus) => {
  switch (status) {
    case KontaxUserStatus.ACTIVATED:
      return "success";
    case KontaxUserStatus.PENDING:
      return "processing";
    case KontaxUserStatus.LEAD:
      return "warning";
    case KontaxUserStatus.DECLINED:
      return "error";
    case KontaxUserStatus.DEACTIVATED:
      return "default";
    default:
      return "default";
  }
};

export const getVariantByDeclarationStatus = (
  status?: NextVatDeclarationStatus
) => {
  switch (status) {
    case NextVatDeclarationStatus.READY_TO_SEND:
      return "#c3e6cb";
    case NextVatDeclarationStatus.PENDING:
      return "#d6d8db";
    case NextVatDeclarationStatus.READY_TO_CHECK:
      return "#ffeeba";
    case NextVatDeclarationStatus.BLOCKED:
      return "#fcd2cf";
    case NextVatDeclarationStatus.READY_TO_BOOK:
      return "#e3d9ff";
    default:
      return "rgba(0,0,0,0.05)";
  }
};

export const getColorByOnboardingStatus = (onbStatus?: OnbStatus | null) => {
  switch (onbStatus) {
    case OnbStatus.COMPLETED:
      return "success";
    case OnbStatus.NOT_SET:
      return "default";
    case OnbStatus.USER_CANCELLATION:
      return "error";
    default:
      return "warning";
  }
};
